import { evaluate } from '@/api/openapi/assessments/audios/evaluations';
import { AudioAssessment } from '@/components/results/AudioAssessment/AudioAssessment';
import { ResultDisplayRecording } from '@/components/results/ResultDisplayRecording';
import { RESULT_DISPLAY_SIZE } from '@/constant';
import { capture } from '@/handlers/error';
import { useSession } from '@/providers/session/SessionProvider';
import { isAdjustedAudioAssessmentResult } from '@/types/pronouciation';
import { isSlideComponentPronunciationAssessment, Question } from '@/types/slide';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../format';
import { ResultContainer, ResultContent, ResultTitle } from '../ResultCard';
import { ResultCorrectness } from '../ResultCorrectness';
import { ResultCardUnsupported } from './ResultCardUnsupported';

export const ResultCardPronunciationAssessment: React.FC<{ question: Question }> = ({ question }) => {
    const { t } = useTranslation();
    const { session } = useSession();

    // ui state
    const [loading, setLoading] = useState<boolean | undefined>(question.answers[0]?.fetchResultFiles ? true : false);
    const [resultFiles, setResultFiles] = useState<{ url: string }[] | undefined>(undefined);
    const [adjustedScores, setAdjustedScores] = useState<Question>(question);
    const [isEvaluationLoading, setIsEvaluationLoading] = useState(false);
    const [evaluationError, setEvaluationError] = useState(false);

    // init
    const audioResultFile = resultFiles?.[0];
    const timeSpent = formatDuration(question.answers.reduce((total, answer) => total + (new Date(answer.endTime).getTime() - new Date(answer.startTime).getTime()), 0) / 1000);
    const answer = question.answers[0];

    const isDataCorrupted = useMemo(() => {
        const answer = question.answers[0];
        if (answer && answer.data) {
            try {
                JSON.parse(answer.data);
                return false;
            } catch (e) {
                capture(e);
                return true;
            }
        }
        return false;
    }, [question]);

    const isMissingAdjustedScores = useMemo(() => {
        if (isDataCorrupted) {
            return false;
        }

        const answer = adjustedScores.answers[0];
        if (answer && answer.data) {
            const parsedAnswer = JSON.parse(answer.data);
            return isAdjustedAudioAssessmentResult(parsedAnswer.result) === false;
        }

        return false;
    }, [isDataCorrupted, adjustedScores.answers]);

    useEffect(() => {
        if (answer?.fetchResultFiles) {
            setLoading(true);
            answer
                .fetchResultFiles()
                .then((files) => {
                    setResultFiles(files);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setResultFiles(undefined);
        }
    }, [answer, question]);

    const getAdjustedScores = useCallback(async () => {
        if (!isSlideComponentPronunciationAssessment(question) || !session?.token) {
            setEvaluationError(true);
            return;
        }

        const answer = question.answers[0];
        if (answer && answer.data && question?.content.refText) {
            try {
                setEvaluationError(false);
                setIsEvaluationLoading(true);

                const data = await evaluate(answer.data, question.content.refText, session?.token);

                if (!data) {
                    setEvaluationError(true);
                    return;
                }

                setAdjustedScores({
                    ...question,
                    answers: question.answers.map((answer) => ({
                        ...answer,
                        data: JSON.stringify({ result: data?.result }),
                    })),
                });
            } catch (error) {
                capture(error);
                setEvaluationError(true);
            } finally {
                setIsEvaluationLoading(false);
            }
        }
    }, [question, session?.token]);

    useEffect(() => {
        if (!isMissingAdjustedScores) {
            return;
        }
        getAdjustedScores();
    }, [getAdjustedScores, isMissingAdjustedScores]);

    if (!isSlideComponentPronunciationAssessment(question)) {
        return <ResultCardUnsupported question={question} />;
    }

    return (
        <>
            <ResultContainer>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex' }}>
                        <ResultTitle variant='h5'>{t('slide.components.pronunciationAssessment.title')}</ResultTitle>
                        <Box sx={{ flexGrow: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 280, gap: 1 }}>
                        <Typography color={question.answers.length > 0 ? 'text.secondary' : 'text.warning'}>{t('result.message.replayRecording')}</Typography>
                        {question.answers.length > 0 && (
                            <Typography color='text.secondary'>
                                {t('result.label.timeSpent')}: {timeSpent}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <ResultContent>
                    <Box sx={{ p: 4, justifyContent: 'center', display: 'flex' }}>
                        {loading || (isMissingAdjustedScores && isEvaluationLoading) ? (
                            <Skeleton variant='rounded' sx={{ width: RESULT_DISPLAY_SIZE.width, height: RESULT_DISPLAY_SIZE.height }} />
                        ) : (
                            <ResultDisplayRecording src={audioResultFile?.url} />
                        )}
                    </Box>
                </ResultContent>
                {isDataCorrupted ? (
                    <Typography color='error' sx={{ p: 1, textAlign: 'center' }}>
                        {t('common.dataCorrupted')}
                    </Typography>
                ) : isMissingAdjustedScores && evaluationError ? (
                    <Stack direction='row' spacing={1} sx={{ p: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Typography color='error'>{t('common.loadingError')}</Typography>
                        <Button size='small' variant='outlined' onClick={getAdjustedScores}>
                            {t('common.retry')}
                        </Button>
                    </Stack>
                ) : !isMissingAdjustedScores && adjustedScores ? (
                    <ResultCorrectness question={adjustedScores} />
                ) : null}
            </ResultContainer>
            {!evaluationError && !isDataCorrupted && !isEvaluationLoading && audioResultFile && <AudioAssessment question={adjustedScores} src={audioResultFile.url} />}
        </>
    );
};
